import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import StickyQuoteBar from '../../components/feature/StickyQuoteBar';

export default function DSCRLoanRatesPage() {
  const [loanAmount, setLoanAmount] = useState(300000);
  const [propertyValue, setPropertyValue] = useState(400000);
  const [rentalIncome, setRentalIncome] = useState(3000);

  const calculateDSCR = () => {
    const monthlyExpenses = (loanAmount * 0.07) / 12;
    return (rentalIncome / monthlyExpenses).toFixed(2);
  };

  const estimatedRate = () => {
    const dscr = parseFloat(calculateDSCR());
    if (dscr >= 1.25) return '6.99% - 7.99%';
    if (dscr >= 1.0) return '7.50% - 8.50%';
    return '8.00% - 9.50%';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <StickyQuoteBar />

      {/* Hero Section - Darker Forest Green */}
      <section className="bg-gradient-to-r from-green-950 via-emerald-950 to-green-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="text-amber-400">DSCR Loan Rates</span> 2026
            </h1>
            <p className="text-xl text-green-100 max-w-3xl mx-auto mb-8">
              Current DSCR loan rates, factors affecting your rate, and how to qualify for the best investment property financing rates available.
            </p>
            <Link
              to="/apply"
              className="inline-flex items-center justify-center bg-gradient-to-r from-amber-500 to-amber-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg hover:shadow-amber-500/30 whitespace-nowrap"
            >
              <i className="ri-percent-line mr-2"></i>
              Get Your Rate Quote
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Interactive Calculator */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-xl p-8 border border-green-300"
          >
            <h2 className="text-3xl font-bold text-green-950 mb-6 text-center">
              DSCR Loan Rate Calculator
            </h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-green-900 mb-2">
                  Loan Amount: ${loanAmount.toLocaleString()}
                </label>
                <input
                  type="range"
                  min="50000"
                  max="5000000"
                  step="10000"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                  className="w-full h-2 bg-green-300 rounded-lg appearance-none cursor-pointer accent-green-800"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-green-900 mb-2">
                  Property Value: ${propertyValue.toLocaleString()}
                </label>
                <input
                  type="range"
                  min="100000"
                  max="10000000"
                  step="10000"
                  value={propertyValue}
                  onChange={(e) => setPropertyValue(Number(e.target.value))}
                  className="w-full h-2 bg-green-300 rounded-lg appearance-none cursor-pointer accent-green-800"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-green-900 mb-2">
                  Monthly Rental Income: ${rentalIncome.toLocaleString()}
                </label>
                <input
                  type="range"
                  min="500"
                  max="50000"
                  step="100"
                  value={rentalIncome}
                  onChange={(e) => setRentalIncome(Number(e.target.value))}
                  className="w-full h-2 bg-green-300 rounded-lg appearance-none cursor-pointer accent-green-800"
                />
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-green-600 mt-8">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="text-center">
                    <p className="text-sm text-green-800 mb-2">Your DSCR</p>
                    <p className="text-4xl font-bold text-green-900">{calculateDSCR()}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-green-800 mb-2">Estimated Rate Range</p>
                    <p className="text-2xl font-bold text-green-950">{estimatedRate()}</p>
                  </div>
                </div>
              </div>

              <Link
                to="/apply"
                className="block w-full bg-green-900 text-white text-center py-4 rounded-xl font-semibold hover:bg-green-800 transition-colors shadow-lg"
              >
                Get Your Personalized Rate Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Current Rates Table */}
      <section className="py-16 bg-gradient-to-br from-green-50 to-emerald-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-green-950 mb-8 text-center">
            Current DSCR Loan Rates by Property Type
          </h2>
          
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-green-950 via-emerald-950 to-green-900 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left">Property Type</th>
                    <th className="px-6 py-4 text-left">DSCR Range</th>
                    <th className="px-6 py-4 text-left">Rate Range</th>
                    <th className="px-6 py-4 text-left">LTV</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-green-200">
                  <tr className="hover:bg-green-50 transition-colors">
                    <td className="px-6 py-4 font-semibold">Single Family Rental</td>
                    <td className="px-6 py-4">1.25+</td>
                    <td className="px-6 py-4 text-green-900 font-semibold">6.99% - 7.99%</td>
                    <td className="px-6 py-4">Up to 80%</td>
                  </tr>
                  <tr className="hover:bg-green-50 transition-colors">
                    <td className="px-6 py-4 font-semibold">Multifamily (2-4 units)</td>
                    <td className="px-6 py-4">1.20+</td>
                    <td className="px-6 py-4 text-green-900 font-semibold">7.25% - 8.25%</td>
                    <td className="px-6 py-4">Up to 75%</td>
                  </tr>
                  <tr className="hover:bg-green-50 transition-colors">
                    <td className="px-6 py-4 font-semibold">Short-Term Rental</td>
                    <td className="px-6 py-4">1.00+</td>
                    <td className="px-6 py-4 text-green-900 font-semibold">7.50% - 8.75%</td>
                    <td className="px-6 py-4">Up to 75%</td>
                  </tr>
                  <tr className="hover:bg-green-50 transition-colors">
                    <td className="px-6 py-4 font-semibold">Multifamily (5+ units)</td>
                    <td className="px-6 py-4">1.25+</td>
                    <td className="px-6 py-4 text-green-900 font-semibold">7.00% - 8.00%</td>
                    <td className="px-6 py-4">Up to 80%</td>
                  </tr>
                  <tr className="hover:bg-green-50 transition-colors">
                    <td className="px-6 py-4 font-semibold">Commercial Property</td>
                    <td className="px-6 py-4">1.30+</td>
                    <td className="px-6 py-4 text-green-900 font-semibold">7.50% - 9.00%</td>
                    <td className="px-6 py-4">Up to 75%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-sm text-green-800 mt-4 text-center">
            *Rates as of January 2025. Actual rates may vary based on credit score, property condition, and market conditions.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-green-950 mb-6">
              Understanding DSCR Loan Rates in 2025
            </h2>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              DSCR loan rates are competitive financing options for real estate investors looking to expand their rental property portfolios. Unlike traditional mortgage rates, DSCR loan rates are primarily determined by the property's ability to generate income rather than the borrower's personal income documentation.
            </p>

            <h3 className="text-2xl font-bold text-green-950 mt-8 mb-4">
              What Affects Your DSCR Loan Rate?
            </h3>
            
            <p className="text-gray-700 mb-4">
              Several key factors influence the interest rate you'll receive on a DSCR loan:
            </p>

            <div className="bg-green-50 rounded-xl p-6 mb-8 border border-green-300">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-800 text-xl mr-3 mt-1"></i>
                  <span><strong>Debt Service Coverage Ratio:</strong> Higher DSCR ratios (1.25+) typically qualify for the best DSCR loan rates, while ratios closer to 1.0 may result in slightly higher rates.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-800 text-xl mr-3 mt-1"></i>
                  <span><strong>Credit Score:</strong> While DSCR loans don't require income verification, your credit score still matters. Scores above 680 generally receive more favorable rates.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-800 text-xl mr-3 mt-1"></i>
                  <span><strong>Loan-to-Value (LTV) Ratio:</strong> Lower LTV ratios demonstrate less risk and often result in better interest rates on your DSCR loan.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-800 text-xl mr-3 mt-1"></i>
                  <span><strong>Property Type:</strong> Single-family rentals typically receive the best rates, while short-term rentals or unique properties may have slightly higher rates.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-800 text-xl mr-3 mt-1"></i>
                  <span><strong>Market Conditions:</strong> Current DSCR loan rates fluctuate with broader economic conditions and Federal Reserve policy decisions.</span>
                </li>
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-green-950 mt-8 mb-4">
              How to Get the Best DSCR Loan Rates
            </h3>
            
            <p className="text-gray-700 mb-4">
              Securing competitive DSCR loan rates requires strategic planning and preparation:
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-green-50 rounded-xl p-6 border border-green-300">
                <h4 className="font-bold text-green-950 mb-3 flex items-center">
                  <i className="ri-line-chart-line text-green-800 text-2xl mr-2"></i>
                  Maximize Your DSCR
                </h4>
                <p className="text-gray-700 text-sm">
                  Increase rental income or reduce debt obligations to improve your debt service coverage ratio. A DSCR of 1.25 or higher unlocks the most competitive rates.
                </p>
              </div>

              <div className="bg-green-50 rounded-xl p-6 border border-green-300">
                <h4 className="font-bold text-green-950 mb-3 flex items-center">
                  <i className="ri-shield-check-line text-green-800 text-2xl mr-2"></i>
                  Improve Credit Score
                </h4>
                <p className="text-gray-700 text-sm">
                  While not as critical as traditional loans, maintaining a credit score above 680 helps you qualify for better DSCR loan rates.
                </p>
              </div>

              <div className="bg-green-50 rounded-xl p-6 border border-green-300">
                <h4 className="font-bold text-green-950 mb-3 flex items-center">
                  <i className="ri-money-dollar-circle-line text-green-800 text-2xl mr-2"></i>
                  Larger Down Payment
                </h4>
                <p className="text-gray-700 text-sm">
                  Putting down 25% or more reduces your LTV ratio and demonstrates financial strength, often resulting in lower interest rates.
                </p>
              </div>

              <div className="bg-green-50 rounded-xl p-6 border border-green-300">
                <h4 className="font-bold text-green-950 mb-3 flex items-center">
                  <i className="ri-search-line text-green-800 text-2xl mr-2"></i>
                  Compare Multiple Lenders
                </h4>
                <p className="text-gray-700 text-sm">
                  DSCR loan rates can vary significantly between lenders. Shopping around and comparing offers ensures you get the best deal.
                </p>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-green-950 mt-8 mb-4">
              DSCR Loan Calculator: How It Works
            </h3>
            
            <p className="text-gray-700 mb-4">
              Our DSCR loan calculator helps you estimate your potential interest rate by analyzing your property's financial performance. The calculator considers your loan amount, property value, and monthly rental income to determine your debt service coverage ratio and corresponding rate range.
            </p>

            <p className="text-gray-700 mb-6">
              To use the DSCR loan calculator effectively, you'll need accurate information about your property's rental income. For existing rentals, use actual lease agreements. For new purchases, obtain a professional rent analysis or appraisal with income estimates.
            </p>

            <h3 className="text-2xl font-bold text-green-950 mt-8 mb-4">
              Comparing DSCR Loan Rates to Traditional Mortgages
            </h3>
            
            <p className="text-gray-700 mb-4">
              While DSCR loan rates may be slightly higher than conventional mortgage rates, they offer significant advantages for real estate investors:
            </p>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 mb-8 border-l-4 border-green-800">
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-green-800 text-xl mr-2"></i>
                  <span><strong>No income verification required</strong> - Perfect for self-employed investors or those with complex tax returns</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-green-800 text-xl mr-2"></i>
                  <span><strong>Unlimited properties</strong> - No restrictions on the number of financed investment properties</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-green-800 text-xl mr-2"></i>
                  <span><strong>Faster approval</strong> - Streamlined underwriting focused on property performance</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-green-800 text-xl mr-2"></i>
                  <span><strong>Flexible qualification</strong> - Based on rental income, not personal debt-to-income ratios</span>
                </li>
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-green-950 mt-8 mb-4">
              Current Market Trends for DSCR Loan Rates
            </h3>
            
            <p className="text-gray-700 mb-4">
              As of 2025, DSCR loan rates remain competitive for real estate investors. The current rate environment reflects several key trends:
            </p>

            <p className="text-gray-700 mb-4">
              Interest rates for DSCR loans typically range from 6.99% to 9.50%, depending on your specific situation. The best DSCR loan rates are reserved for properties with strong cash flow, experienced investors with good credit, and lower loan-to-value ratios.
            </p>

            <p className="text-gray-700 mb-6">
              Many lenders now offer competitive DSCR loan rates for short-term rentals and Airbnb properties, recognizing the higher income potential of these investments. However, these properties may require slightly higher DSCR ratios (1.0 to 1.25) to qualify for the best rates.
            </p>

            <h3 className="text-2xl font-bold text-green-950 mt-8 mb-4">
              Fixed vs. Adjustable DSCR Loan Rates
            </h3>
            
            <p className="text-gray-700 mb-4">
              When comparing DSCR loan rates, you'll encounter both fixed-rate and adjustable-rate options:
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="border-2 border-green-600 rounded-xl p-6 bg-green-50/30">
                <h4 className="font-bold text-green-950 mb-3 text-lg">Fixed-Rate DSCR Loans</h4>
                <p className="text-gray-700 text-sm mb-4">
                  Lock in your interest rate for the entire loan term, providing payment stability and protection against rising rates.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-700 mr-2 mt-0.5"></i>
                    <span>Predictable monthly payments</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-700 mr-2 mt-0.5"></i>
                    <span>Protection from rate increases</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-700 mr-2 mt-0.5"></i>
                    <span>Easier long-term planning</span>
                  </li>
                </ul>
              </div>

              <div className="border-2 border-green-600 rounded-xl p-6 bg-green-50/30">
                <h4 className="font-bold text-green-950 mb-3 text-lg">Adjustable-Rate DSCR Loans</h4>
                <p className="text-gray-700 text-sm mb-4">
                  Start with lower initial rates that adjust periodically based on market conditions.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-700 mr-2 mt-0.5"></i>
                    <span>Lower initial interest rates</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-700 mr-2 mt-0.5"></i>
                    <span>Potential for rate decreases</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-700 mr-2 mt-0.5"></i>
                    <span>Good for short-term holds</span>
                  </li>
                </ul>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-green-950 mt-8 mb-4">
              Frequently Asked Questions About DSCR Loan Rates
            </h3>

            <div className="space-y-6 mb-8">
              <div className="bg-green-50 rounded-xl p-6">
                <h4 className="font-bold text-green-950 mb-2">What are current DSCR loan rates?</h4>
                <p className="text-gray-700">
                  Current DSCR loan rates range from 6.99% to 9.50%, depending on your DSCR ratio, credit score, property type, and loan-to-value ratio. Properties with DSCR ratios of 1.25 or higher typically qualify for the best rates.
                </p>
              </div>

              <div className="bg-green-50 rounded-xl p-6">
                <h4 className="font-bold text-green-950 mb-2">Are DSCR loan rates higher than conventional rates?</h4>
                <p className="text-gray-700">
                  DSCR loan rates may be slightly higher than conventional mortgage rates, typically by 0.5% to 1.5%. However, the benefits of no income verification, unlimited properties, and faster approval often outweigh the modest rate difference for investors.
                </p>
              </div>

              <div className="bg-green-50 rounded-xl p-6">
                <h4 className="font-bold text-green-950 mb-2">How can I get the lowest DSCR loan rate?</h4>
                <p className="text-gray-700">
                  To secure the lowest DSCR loan rate, aim for a DSCR of 1.25 or higher, maintain a credit score above 680, make a larger down payment (25%+), and compare offers from multiple lenders specializing in DSCR loans.
                </p>
              </div>

              <div className="bg-green-50 rounded-xl p-6">
                <h4 className="font-bold text-green-950 mb-2">Do DSCR loan rates vary by property type?</h4>
                <p className="text-gray-700">
                  Yes, DSCR loan rates can vary based on property type. Single-family rentals typically receive the best rates, while short-term rentals, multifamily properties, and commercial properties may have slightly different rate structures.
                </p>
              </div>

              <div className="bg-green-50 rounded-xl p-6">
                <h4 className="font-bold text-green-950 mb-2">Can I refinance to get a better DSCR loan rate?</h4>
                <p className="text-gray-700">
                  Absolutely! If your property's DSCR has improved, market rates have decreased, or your credit score has increased, refinancing your DSCR loan can help you secure a lower interest rate and reduce your monthly payments.
                </p>
              </div>

              <div className="bg-green-50 rounded-xl p-6">
                <h4 className="font-bold text-green-950 mb-2">How accurate is the DSCR loan calculator?</h4>
                <p className="text-gray-700">
                  Our DSCR loan calculator provides estimated rate ranges based on typical market conditions. Your actual rate will depend on additional factors including your complete financial profile, property details, and current lender offerings. Contact us for a personalized rate quote.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-950 via-emerald-950 to-green-900 text-white rounded-2xl p-8 text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Ready to Lock in Your DSCR Loan Rate?</h3>
              <p className="text-green-100 mb-6 max-w-2xl mx-auto">
                Get a personalized rate quote from top DSCR lenders nationwide. Our network of specialized lenders competes for your business, ensuring you receive competitive rates and terms.
              </p>
              <Link
                to="/apply"
                className="inline-block bg-white text-green-950 px-8 py-4 rounded-xl font-semibold hover:bg-green-50 transition-colors shadow-lg"
              >
                Get Your Free Rate Quote
              </Link>
            </div>
          </article>
        </div>
      </section>

      <Footer />
    </div>
  );
}